import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import ReactDOMServer from 'react-dom/server';

interface ScorecardData {
  completed: any[];
}

const tableBaseStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  overflow: 'auto',
};

const thTdStyle: React.CSSProperties = {
  border: '1px solid #E9EBF4',
  padding: '10px',
  textAlign: 'left',
  minWidth: '80px',
  fontSize: '14px',
};

const thStyle: React.CSSProperties = {
  ...thTdStyle,
  color: '#413B74',
  fontWeight: 550,
};

const getCellStyle = (
  isFirstColumn: boolean,
  isLastColumn: boolean,
  isLastRow: boolean = false
): React.CSSProperties => {
  return {
    ...thTdStyle,
    borderLeft: isFirstColumn ? 'none' : '1px solid #E9EBF4',
    borderRight: isLastColumn ? 'none' : '1px solid #E9EBF4',
    borderBottom: isLastRow ? 'none' : '1px solid #E9EBF4',
  };
};

const getHeaderStyle = (
  isFirstColumn: boolean,
  isLastColumn: boolean
): React.CSSProperties => {
  return {
    ...thStyle,
    borderLeft: isFirstColumn ? 'none' : '1px solid #E9EBF4',
    borderRight: isLastColumn ? 'none' : '1px solid #E9EBF4',
  };
};

const ScorecardTableContent = ({ node }: any) => {
  const scorecardData = node.attrs.data as ScorecardData;
  const completed = scorecardData?.completed || [];

  if (completed?.length === 0) {
    return null;
  }

  return (
    <NodeViewWrapper>
      <div
        style={{
          borderRadius: '4px',
          marginBottom: '8px',
          marginTop: '8px',
          border: '1px solid #E9EBF4',
        }}
      >
        <div
          style={{
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
            fontWeight: 550,
            color: '#413B74',
            backgroundColor: '#F8F9FC',
            padding: '10px 12px',
          }}
        >
          Scorecard History
        </div>
        <table style={tableBaseStyle}>
          <thead>
            <tr>
              <th style={getHeaderStyle(true, false)}>Interviewer/Reviewer</th>
              <th style={getHeaderStyle(false, false)}>Rating</th>
              <th style={getHeaderStyle(false, true)}>Activity</th>
            </tr>
          </thead>
          <tbody>
            {completed.map((scorecard: any, idx: number) => {
              const isLastRow = idx === completed.length - 1;
              return (
                <tr key={idx}>
                  <td style={getCellStyle(true, false, isLastRow)}>
                    {scorecard?.reviewer?.name || '-'}
                  </td>
                  <td style={getCellStyle(false, false, isLastRow)}>
                    {scorecard?.rating
                      ? scorecard.rating
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (l: string) => l.toUpperCase())
                      : '-'}
                  </td>
                  <td style={getCellStyle(false, true, isLastRow)}>
                    {scorecard?.ats_job_stage_action?.name || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </NodeViewWrapper>
  );
};

export const ScorecardTableExtension = Node.create({
  name: 'ScorecardTable',

  group: 'block',

  atom: true,
  draggable: true,

  parseHTML() {
    return [
      {
        tag: 'scorecard-table-content',
      },
      {
        // Parse raw HTML tables that contain scorecard data
        tag: 'table',
        getAttrs: (element) => {
          const table = element as HTMLTableElement;
          // Check if this is a scorecard table by looking for the header columns
          const headers = table.querySelectorAll('thead th');
          if (headers.length !== 3) return false;

          const headerTexts = Array.from(headers).map((h) =>
            h.textContent?.trim()
          );
          if (
            !headerTexts.includes('Interviewer/Reviewer') ||
            !headerTexts.includes('Rating') ||
            !headerTexts.includes('Activity')
          ) {
            return false;
          }

          return {};
        },
      },
    ];
  },

  addAttributes() {
    return {
      data: {
        default: null,
        parseHTML: (element) => {
          // First try to get data from the custom attribute
          const dataAttr = element.getAttribute('data-scorecard');
          if (dataAttr) {
            return JSON.parse(dataAttr);
          }

          // If it's a raw table, extract data from the table cells
          if (element.tagName === 'TABLE') {
            const rows = element.querySelectorAll('tbody tr');
            const completed = Array.from(rows).map((row) => {
              const cells = row.querySelectorAll('td');
              return {
                reviewer: { name: cells[0]?.textContent?.trim() || '-' },
                rating:
                  cells[1]?.textContent
                    ?.trim()
                    ?.toLowerCase()
                    .replace(/ /g, '_') || '',
                ats_job_stage_action: {
                  name: cells[2]?.textContent?.trim() || '-',
                },
              };
            });
            return { completed };
          }

          return null;
        },
        renderHTML: (attributes) => {
          if (!attributes.data) {
            return {};
          }
          return {
            'data-scorecard': JSON.stringify(attributes.data),
          };
        },
      },
    };
  },

  renderHTML({ node }) {
    const scorecardData = node.attrs.data as ScorecardData;
    const completed = scorecardData?.completed || [];

    // Generate the HTML table markup
    const htmlString = generateScorecardTableHTML(completed);

    if (typeof document !== 'undefined') {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      return tempDiv.firstChild as HTMLElement;
    }

    // Fallback: return custom tag if document is not available (shouldn't happen in practice)
    return [
      'scorecard-table-content',
      { 'data-scorecard': JSON.stringify(scorecardData) },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ScorecardTableContent);
  },
});

// Helper function to generate static HTML for email sending
export const generateScorecardTableHTML = (completed: any[]) => {
  if (!completed || completed.length === 0) {
    return '<p>There are no completed scorecards to insert.</p>';
  }

  const tableHTML = ReactDOMServer.renderToStaticMarkup(
    <div
      style={{
        border: '1px solid #E9EBF4',
        borderRadius: '4px',
        marginBottom: '8px',
        marginTop: '8px',
      }}
    >
      <table style={tableBaseStyle}>
        <thead>
          <tr>
            <th style={getHeaderStyle(true, false)}>Interviewer/Reviewer</th>
            <th style={getHeaderStyle(false, false)}>Rating</th>
            <th style={getHeaderStyle(false, true)}>Activity</th>
          </tr>
        </thead>
        <tbody>
          {completed.map((scorecard: any, idx: number) => {
            const isLastRow = idx === completed.length - 1;
            return (
              <tr key={idx}>
                <td style={getCellStyle(true, false, isLastRow)}>
                  {scorecard?.reviewer?.name || '-'}
                </td>
                <td style={getCellStyle(false, false, isLastRow)}>
                  {scorecard?.rating
                    ? scorecard.rating
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (l: string) => l.toUpperCase())
                    : '-'}
                </td>
                <td style={getCellStyle(false, true, isLastRow)}>
                  {scorecard?.ats_job_stage_action?.name || '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return tableHTML;
};
